import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { EmptyState, ErrorState, ListaSelecao, Modal, Skeleton } from '../../../ui';

import { RootState } from '../../../store/store';
import { setCodigoRegionalSelecionada } from '../../../store/cotacaoSlice';
import { RegionalVinculada, buscarUsuarioPorId } from '../../../api/user/userApi';
import { extrairMensagemErro } from '../../../utils/apiError';

/**
 * Escolha da regional do consultor (reescrito na at-58 sobre o `Modal` do kit, a
 * pedido de Victor em 09/09/2026).
 *
 * ⚠️ **É componente de DOMÍNIO**, e por isso mora em `components/`: lê o `userId`
 * do Redux, chama a API de usuário e grava um efeito colateral no fluxo de
 * cotação. O que ele reaproveita do kit — `Modal`, `ListaSelecao`, `Skeleton`,
 * `EmptyState`, `ErrorState` — é puro. Precedente do `ShellSaac`.
 *
 * ## O que a reescrita mudou
 *
 *   1. **Visual do design system** (era `.modal-overlay`/`.modal-content`, com
 *      título laranja centralizado e `<li onClick>` sem estilo de lista).
 *   2. **A lista é navegável pelo teclado.** Eram `<li onClick>`: `li` não recebe
 *      foco, não responde a Enter e não é anunciado como acionável — a escolha da
 *      regional era **impossível sem mouse**. Agora são `<button>` (via
 *      `ListaSelecao`), e o foco entra no primeiro item ao abrir.
 *   3. **Falha deixou de ser muda.** O `catch` mostrava um toast e deixava a
 *      lista **vazia** — indistinguível de "você não tem regional vinculada", que
 *      é um problema com outra solução. Agora há `ErrorState` com "Tentar
 *      novamente" (convenção da at-56: falha muda e spinner eterno são o mesmo
 *      defeito).
 *   4. **O vazio explica o que fazer** (spec 03 §3.4): sem regional vinculada o
 *      consultor **não consegue seguir** com a adesão, e o vínculo é feito pelo TI
 *      em `/ti/usuarios`. Antes era só "Nenhuma regional vinculada encontrada." —
 *      um beco sem saída sem explicação.
 *   5. **Esc, clique fora e foco** vêm do `Modal`. O clique fora existia (por
 *      `mousedown` no document, que fecha até num arrasto começado dentro do
 *      diálogo); Esc e devolução de foco, não.
 *
 * ## O que NÃO mudou, de propósito
 *
 * ⚠️ **As props são as mesmas** (`isOpen`/`onClose`/`onSelectRegional`, em
 * inglês). 📌 **Atualização da at-62:** `views/cotacao` foi migrada e não há mais
 * consumidor legado — traduzi-las é da frente D.
 * ⚠️ **O `setCodigoRegionalSelecionada` continua sendo despachado**, e a at-62
 * **não** o removeu de propósito: é assim que `codigoRegionalSelecionada` chega
 * ao slice, e `/cotacao-produtos` lê aquele campo para buscar os produtos do
 * perfil. A tela nova também guarda a regional no estado local — o despacho
 * daqui deixou de ser a **única** fonte, mas continua sendo a que abastece o
 * slice enquanto o consultor não envia o formulário.
 * ⚠️ **`import '../regionais/ModalRegionais.css'` foi removido** — nenhuma classe
 * de lá é mais usada aqui. É seguro porque `ModalCooperativasUsuario` **ainda
 * importa** aquele arquivo, e ele carrega dois globais acidentais dos quais o app
 * depende: **`.carregando` (36 telas)** e **`.mt-20` (10 telas)**, definidos SÓ
 * ali. 🔴 Quem migrar o modal de cooperativas — o último importador — tem de
 * resgatar os dois antes, ou 46 telas perdem estilo em silêncio.
 */

/*
 * ⚠️ O tipo vem do `userApi` desde a at-68, e não mais declarado aqui: a cópia local
 * dizia `cidade: string` e `estado: string` como OBRIGATÓRIOS, mas as duas colunas são
 * nuláveis no espelho do SGA. O `any` que `buscarUsuarioPorId` devolvia escondia a
 * divergência; ao tipar a função, ela apareceu. O uso já tratava a ausência
 * (`[cidade, estado].filter(Boolean)`), então nada muda na tela.
 */
type Regional = RegionalVinculada;

interface ModalRegionaisUsuarioProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectRegional: (regional: Regional) => void;
}

type Estado = 'carregando' | 'ok' | 'erro';

const ModalRegionaisUsuario: React.FC<ModalRegionaisUsuarioProps> = ({
  isOpen,
  onClose,
  onSelectRegional,
}) => {
  const dispatch = useDispatch();
  const userId = useSelector((state: RootState) => state.auth.userId);

  const [regionais, setRegionais] = useState<Regional[]>([]);
  const [estado, setEstado] = useState<Estado>('carregando');
  const [erro, setErro] = useState('');

  const buscar = useCallback(async () => {
    if (!userId) return;

    setEstado('carregando');
    try {
      const usuario = await buscarUsuarioPorId(userId);

      // Resposta sem o array é resposta inesperada, não lista vazia — tratar as
      // duas como "nenhuma regional" esconderia um problema de contrato.
      if (!usuario || !Array.isArray(usuario.regionais)) {
        setErro('A resposta do servidor não trouxe as regionais do seu usuário.');
        setEstado('erro');
        return;
      }

      setRegionais(usuario.regionais);
      setEstado('ok');
    } catch (falha) {
      setErro(extrairMensagemErro(falha, 'Não foi possível carregar as suas regionais.'));
      setEstado('erro');
    }
  }, [userId]);

  useEffect(() => {
    if (isOpen) void buscar();
  }, [isOpen, buscar]);

  const escolher = (regional: Regional) => {
    // Efeito colateral legado do fluxo de cotação — ver o aviso no topo.
    dispatch(setCodigoRegionalSelecionada(regional.codigo_regional));
    onSelectRegional(regional);
    onClose();
  };

  return (
    <Modal
      aberto={isOpen}
      titulo="Selecionar Regional"
      descricao="Define cota, produtos e rateio do veículo nas próximas etapas."
      tamanho="sm"
      aoFechar={onClose}
    >
      {estado === 'carregando' && <Skeleton linhas={3} altura="tw-h-12" />}

      {estado === 'erro' && (
        <ErrorState mensagem={erro} aoTentarNovamente={() => void buscar()} />
      )}

      {estado === 'ok' && (
        <ListaSelecao
          itens={regionais}
          chave={(r) => r.id ?? r.codigo_regional}
          rotulo={(r) => r.nome}
          /* Cidade/UF distinguem regionais de nome parecido — a resposta já
             trazia os campos e a lista antiga os descartava. */
          detalhe={(r) => [r.cidade, r.estado].filter(Boolean).join(' / ') || undefined}
          aoEscolher={escolher}
          rotuloAcessivel="Regionais vinculadas ao seu usuário"
          vazio={
            <EmptyState
              icone="local"
              titulo="Nenhuma regional vinculada ao seu usuário"
              descricao="Sem uma regional você não consegue concluir a adesão. Peça ao TI para vincular a sua regional em Administração › Usuários."
            />
          }
        />
      )}
    </Modal>
  );
};

export default ModalRegionaisUsuario;
